/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { SmsContent } from "../types";
import { CHANNEL_COST_CENTS, STUB_SHOP } from "../stub";
import { CharCounter, Field, formatMoney, PhoneFrame, SampleCustomerPicker, TemplateTextarea, usePreview } from "../shared";
import { SMS_OPT_OUT_SUFFIX, smsSegments } from "../variables";
import { toGsmSafe } from "../richText";

type SmsEditorProps = {
  content: SmsContent;
  onChange: (content: SmsContent) => void;
};

const SENDER_ID_MAX = 11;

// What the customer actually receives: rendered body, shortened links, opt-out line.
export const renderSms = (content: SmsContent, render: (s: string) => string) => {
  let text = render(content.body);
  if (content.shorten_links) text = text.replace(/https?:\/\/\S+/g, "lmn.to/x7Kq2");
  if (content.append_opt_out) text += SMS_OPT_OUT_SUFFIX;
  return text;
};

export const SmsEditor = defineComponent<SmsEditorProps>({
  name: "SmsEditor",
  props: ["content", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const preview = usePreview();
    const patch = (p: Partial<SmsContent>) => props.onChange({ ...props.content, ...p });

    const rendered = computed(() => renderSms(props.content, preview.render));
    const info = computed(() => smsSegments(rendered.value));

    const Meter = () => {
      const i = info.value;
      return (
        <div class="grid grid-cols-3 gap-2 text-center">
          <div class="rounded-lg bg-base-200 p-2">
            <div class="text-xs text-base-content/50">{t("campaigns.studio_sms.encoding")}</div>
            <div class={["font-semibold", i.encoding === "UCS-2" && "text-warning"]}>{i.encoding}</div>
          </div>
          <div class="rounded-lg bg-base-200 p-2">
            <div class="text-xs text-base-content/50">{t("campaigns.studio_sms.segments")}</div>
            <div class={["font-semibold", i.segments > 2 && "text-warning"]}>{i.segments}</div>
          </div>
          <div class="rounded-lg bg-base-200 p-2">
            <div class="text-xs text-base-content/50">{t("campaigns.studio_sms.cost_per_recipient")}</div>
            <div class="font-semibold">{formatMoney(i.segments * CHANNEL_COST_CENTS.sms)}</div>
          </div>
        </div>
      );
    };

    return () => {
      const c = props.content;
      const i = info.value;
      return (
        <div class="flex h-full min-h-0 overflow-hidden">
          <div class="min-w-0 flex-1 space-y-4 overflow-y-auto p-6">
            <Field
              label={t("campaigns.studio_sms.sender_id")}
              hint={t("campaigns.studio_sms.sender_id_hint")}
              counter={CharCounter(c.sender_id.length, SENDER_ID_MAX)}
            >
              <input
                type="text"
                class="input input-bordered input-sm w-full font-mono"
                maxlength={SENDER_ID_MAX}
                value={c.sender_id}
                onInput={(e) => patch({ sender_id: (e.target as HTMLInputElement).value.replace(/[^A-Za-z0-9 ]/g, "") })}
              />
            </Field>

            <Field
              label={t("campaigns.studio_sms.message")}
              counter={t("campaigns.studio_sms.remaining", [i.remaining, i.segments])}
            >
              <TemplateTextarea channel="sms" rows={6} value={c.body} onChange={(v) => patch({ body: v })} />
            </Field>

            {Meter()}

            {i.encoding === "UCS-2" && (
              <div role="alert" class="alert alert-warning py-2 text-sm">
                <span class="material-symbols-outlined">warning</span>
                <div class="min-w-0 flex-1">
                  {t("campaigns.studio_sms.unicode_warning", [i.nonGsmChars.join(" ")])}
                </div>
                <button
                  type="button"
                  class="btn btn-sm"
                  onClick={() => patch({ body: toGsmSafe(c.body, i.nonGsmChars) })}
                >
                  {t("campaigns.studio_sms.make_gsm_safe")}
                </button>
              </div>
            )}

            <div class="space-y-1 rounded-xl border border-base-300 p-3">
              <label class="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  class="toggle toggle-primary toggle-sm"
                  checked={c.append_opt_out}
                  onChange={(e) => patch({ append_opt_out: (e.target as HTMLInputElement).checked })}
                />
                <span class="label-text">{t("campaigns.studio_sms.append_opt_out")}</span>
              </label>
              {!c.append_opt_out && (
                <p class="pl-12 text-xs text-error">{t("campaigns.studio_sms.opt_out_required")}</p>
              )}
              <label class="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  class="toggle toggle-primary toggle-sm"
                  checked={c.shorten_links}
                  onChange={(e) => patch({ shorten_links: (e.target as HTMLInputElement).checked })}
                />
                <span class="label-text">{t("campaigns.studio_sms.shorten_links")}</span>
              </label>
            </div>
          </div>

          <div class="flex w-[380px] shrink-0 flex-col items-center gap-4 overflow-y-auto border-l border-base-300 bg-base-200 p-6">
            <SampleCustomerPicker />
            <PhoneFrame>
              <div class="flex shrink-0 flex-col items-center gap-1 border-b border-base-300 pb-2 pt-1">
                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-base-300 font-bold">
                  {(c.sender_id || STUB_SHOP.name).slice(0, 1)}
                </div>
                <div class="text-xs font-medium">{c.sender_id || "—"}</div>
              </div>
              <div class="min-h-0 flex-1 overflow-y-auto p-3">
                <div class="mb-2 text-center text-[10px] text-base-content/50">{t("campaigns.studio_sms.today_time")}</div>
                <div class="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-bl-md bg-base-300 px-3 py-2 text-[13px] leading-snug">
                  {rendered.value}
                </div>
              </div>
            </PhoneFrame>
          </div>
        </div>
      );
    };
  },
});
